import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Divider,
  Badge,
} from "@chakra-ui/react";
import { CheckCircleIcon, ArrowBackIcon } from "@chakra-ui/icons";
import axios from "axios";

const SubmitResults = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Load answers from localStorage
        const storedAnswers = localStorage.getItem(`answers_${uuid}`);
        if (storedAnswers) {
          setAnswers(JSON.parse(storedAnswers));
        }

        // Fetch questions
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/api/evaluate/${uuid}/questions`
        );
        
        // Log the complete questions response to check the structure
        console.log('Questions API response:', JSON.stringify(response.data, null, 2));
        
        setQuestions(response.data);
        setLoading(false);
      } catch (error) {
        if (error?.response?.status === 409) {
          navigate(`/evaluate/${uuid}/welcome`, { replace: true });
          return;
        }
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [uuid, navigate]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Debug the questions to see their structure
      console.log("Questions structure:", questions);
      
      const payload = {
        start_at: localStorage.getItem(`startTime_${uuid}`),
        completed_at: new Date().toISOString(),
        answers: questions.map(question => {
          const userAnswer = answers[question.questionNumber] || '';
          console.log(`Question ${question.questionNumber}:`, question);
          console.log(`User answer:`, userAnswer);
          console.log(`For question ${question.questionNumber}, using ID: ${question.id}`);
          
          let responseText = '';
          
          if (question.questionType === 'OPEN' || question.questionType === 'CODING' || question.questionType === 'CODE_COMPLETION') {
            // For text-based answers (OPEN, CODING, CODE_COMPLETION), use answer directly
            responseText = userAnswer;
          } 
          else if (question.questionType === 'QCM') {
            // For single-choice questions
            if (userAnswer && userAnswer.startsWith('option')) {
              const optionNum = userAnswer.replace('option', '');
              responseText = question[`answer${optionNum}`] || userAnswer;
              console.log(`QCM option ${optionNum} text:`, responseText);
            } else {
              responseText = userAnswer;
            }
          }
          else if (question.questionType === 'MULTI-CHOICE') {
            // For multi-choice questions
            if (userAnswer && userAnswer.includes(',')) {
              responseText = userAnswer.split(',')
                .map(opt => {
                  if (opt && opt.startsWith('option')) {
                    const optionNum = opt.trim().replace('option', '');
                    const answerText = question[`answer${optionNum}`];
                    console.log(`MULTI option ${optionNum} text:`, answerText);
                    return answerText || opt;
                  }
                  return opt;
                })
                .filter(Boolean)
                .join(', ');
            } else if (userAnswer && userAnswer.startsWith('option')) {
              const optionNum = userAnswer.replace('option', '');
              responseText = question[`answer${optionNum}`] || userAnswer;
            } else {
              responseText = userAnswer;
            }
          }

          // Log the question ID to verify it exists
          console.log('Question ID being added:', question.id);
          
          return {
            question: question.questionText,
            question_id: question.questionId,
            user_response: responseText || userAnswer
          };
        })
      };

      console.log("Final payload:", payload);
      console.log("Final payload as JSON string:", JSON.stringify(payload));

      const filteredPayload = {
        ...payload,
        answers: payload.answers.map(answer => {
          if (!answer.question_id) {
            console.error('Missing question_id for question:', answer.question);
          }
          return answer;
        })
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/api/evaluate/${uuid}/response`,
        filteredPayload
      );

      if (response.status === 200) {
        localStorage.removeItem(`answers_${uuid}`);
        localStorage.removeItem(`startTime_${uuid}`);
        localStorage.removeItem(`currentQuestion_${uuid}`);
        setIsSubmitted(true);
      }
    } catch (error) {
      if (error?.response?.status === 409) {
        navigate(`/evaluate/${uuid}/welcome`, { replace: true });
        return;
      }
      console.error('Submission error:', error);
      if (error.response && error.response.status === 400) {
        setSubmitError(error.response.data.message || 'Invalid submission data. Please check your answers.');
      } else {
        setSubmitError('Submission failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.md">
          <Center h="60vh">
            <VStack spacing={4}>
              <Spinner size="xl" color="purple.500" thickness="4px" />
              <Text color="gray.600">Loading evaluation results...</Text>
            </VStack>
          </Center>
        </Container>
      </Box>
    );
  }

  if (isSubmitted) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.md">
          <Center h="60vh">
            <Alert
              status="success"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="200px"
              borderRadius="lg"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <Heading size="md" mt={4} mb={2}>
                Evaluation Submitted Successfully
              </Heading>
              <Text mb={4}>Thank you for completing the evaluation.</Text>
              <Button 
                bg="rgb(37, 87, 167)"
                color="white"
                _hover={{ bg: "rgb(27, 67, 137)" }}
                onClick={() => navigate('/')}
              >
                Return Home
              </Button>
            </Alert>
          </Center>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Container maxW="container.lg">
        <Box bg="white" borderRadius="lg" borderWidth="1px" p={8} shadow="sm">
          <VStack spacing={6} align="stretch">
            <Heading size="lg" color="black">
              Review Your Answers
            </Heading>
            
            {submitError && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <Box>
                  <Heading size="sm">Submission Error</Heading>
                  <Text fontSize="sm" mt={1}>{submitError}</Text>
                </Box>
              </Alert>
            )}
            
            <Box>
              <Text fontSize="md" color="gray.700" mb={2}>
                You answered {Object.keys(answers).length} of {questions.length} questions.
              </Text>
              {Object.keys(answers).length < questions.length && (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">Note: Unanswered questions will be marked as incorrect.</Text>
                </Alert>
              )}
            </Box>
            
            <Divider />
            
            <Box>
              <Heading size="md" mb={4}>Your Answers:</Heading>
              <VStack spacing={4} align="stretch">
                {questions.map(question => (
                  <Box
                    key={question.questionNumber}
                    borderWidth="1px"
                    borderRadius="md"
                    p={4}
                    bg="gray.50"
                  >
                    <HStack spacing={3} mb={2} align="flex-start">
                      <Badge colorScheme="purple" fontSize="sm">
                        Q{question.questionNumber}
                      </Badge>
                      <Text fontWeight="medium" color="gray.800" flex="1">
                        {question.questionText}
                      </Text>
                    </HStack>
                    <Box pl={8}>
                      {answers[question.questionNumber] ? (
                        <Text color="gray.700" fontSize="sm">
                          {(() => {
                            const userAnswer = answers[question.questionNumber];
                            
                            // For QCM questions (single choice)
                            if (question.questionType === 'QCM' && userAnswer.startsWith('option')) {
                              const optionNum = userAnswer.replace('option', '');
                              return question[`answer${optionNum}`] || userAnswer;
                            }
                            
                            // For MULTI-CHOICE questions (multiple choices)
                            if (question.questionType === 'MULTI-CHOICE' && userAnswer.includes(',')) {
                              return userAnswer.split(',')
                                .map(opt => {
                                  if (opt.startsWith('option')) {
                                    const optionNum = opt.trim().replace('option', '');
                                    return question[`answer${optionNum}`] || opt;
                                  }
                                  return opt;
                                })
                                .filter(Boolean)
                                .join(', ');
                            }
                            
                            // For all other cases
                            return userAnswer;
                          })()}
                        </Text>
                      ) : (
                        <Text color="gray.400" fontSize="sm" fontStyle="italic">
                          No answer provided
                        </Text>
                      )}
                    </Box>
                  </Box>
                ))}
              </VStack>
            </Box>
            
            <Divider />
            
            <HStack spacing={4} justify="flex-end">
              <Button
                leftIcon={<ArrowBackIcon />}
                variant="outline"
                onClick={() => navigate(`/evaluate/${uuid}/q/${questions.length}`)}
                isDisabled={isSubmitting}
              >
                Back
              </Button>
              
              <Button
                rightIcon={<CheckCircleIcon />}
                bg="rgb(37, 87, 167)"
                color="white"
                _hover={{ bg: "rgb(27, 67, 137)" }}
                _active={{ bg: "rgb(17, 47, 107)" }}
                onClick={handleSubmit}
                isLoading={isSubmitting}
                loadingText="Submitting..."
              >
                Submit Evaluation
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default SubmitResults; 
